import {
  capitalizeFirstLetter,
  CommonPlateformXDialog,
  EMPTY_SCHEDULE,
  getScheduleConfirmation,
  Loader,
  planScheduleChanges,
  scheduleFromTriggers,
  ScheduleValue,
  ShowToastError,
  ShowToastSuccess,
  validateScheduleChanges,
} from "@platformx/utilities";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { applyContentFieldDefaults } from "../utils/contentFieldDefaults";
import {
  applyScheduleEdits,
  calculateInitialValues,
  calculateInitialValuesWithData,
  createContentType,
  fetchSchema,
  fetchSchemaWithData,
  getScheduleErrorText,
  publishContentType,
  publishScheduledContent,
  updateContentType,
} from "../utils/helperFns";
import { DynamicComponent } from "./DynamicComponent/DynamicComponent";

export const DynamicComponentContainer = () => {
  const { documentId } = useParams();
  const searchPageUrl = new URL(window.location.href);
  const subCategory: string = searchPageUrl?.pathname?.split("/")?.[5];
  const category: string = searchPageUrl?.pathname?.split("/")?.[4];
  const [template, setTemplate] = useState<any>({});
  const [contentInstance, setContentInstance] = useState<any>({});
  const [showPublishConfirm, setShowPublishConfirm] = useState(false);
  const [isDraft, setIsDraft] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const savedData = useRef<{
    id: string;
    name: string;
    canonical?: { path: string; slug: string };
  }>({ id: "", name: "" });

  //Publish Success Dialog Close Handle
  const handleCloseDialog = () => {
    setShowPublishConfirm(false);
  };

  // KIWI-6576: schedule publish/unpublish. Kept in its own state, NOT in contentInstance — the save
  // mutations validate every key against the schema and reject unknown ones. Applied at Publish; a
  // draft save does not persist it.
  const [schedule, setSchedule] = useState<ScheduleValue>(EMPTY_SCHEDULE);
  const [scheduleErrors, setScheduleErrors] = useState<{ publish?: string; unpublish?: string }>(
    {},
  );
  // Schedules that already exist on the server, read when an item is opened for edit. The form is seeded
  // with them and those sides are locked (KIWI-6762): they are changed only from the content list menu.
  // Publish still diffs against `initialSchedule`, so an existing side is never sent again (the backend
  // rejects a second schedule on a side that already has one).
  const initialSchedule = useRef<ScheduleValue>(EMPTY_SCHEDULE);
  const [pendingSchedule, setPendingSchedule] = useState<{
    publish?: string | null;
    unpublish?: string | null;
  }>({});
  // Scheduling is for content types only. A collection shares this container, so it must keep the
  // plain validate-free, unscheduled publish it always had, whatever the schedule state holds.
  const isCollection = template?.schema_document_type === "collection";

  const handleScheduleChange = (next: ScheduleValue) => {
    setSchedule(next);
    setScheduleErrors({});
  };

  const publishNow = async () => {
    const response: any = await publishContentType(
      savedData,
      subCategory,
      template?.schema_document_type,
    );
    if (response?.authoring_publishSchemaContent?.message === "Published successfully!!!") {
      setIsLoading(false);
      setShowPublishConfirm(true);
    }
  };

  // Publishes the item that was just saved. With nothing scheduled, now or before, this is exactly the
  // request that was always sent. Otherwise: first move or cancel the schedules that already existed,
  // then schedule the new ones, all through the rejecting API so a failure cannot pass as success. A
  // publish schedule that is still in place means the item must NOT also be published right now.
  const publishSavedContent = async () => {
    if (isCollection) {
      await publishNow();
      return;
    }
    const { creates, edits } = planScheduleChanges(initialSchedule.current, schedule);
    const hasNew = creates.isSchedulePublish || creates.isScheduleUnpublish;
    if (!hasNew && edits.length === 0 && !schedule.isSchedulePublish) {
      await publishNow();
      return;
    }
    if (edits.length > 0) {
      await applyScheduleEdits(savedData, subCategory, template?.schema_document_type, edits);
    }
    if (hasNew) {
      await publishScheduledContent(
        savedData,
        subCategory,
        template?.schema_document_type,
        creates,
        schedule.isSchedulePublish && !creates.isSchedulePublish,
      );
    } else if (!schedule.isSchedulePublish) {
      // A scheduled publish was cancelled and none remains: publish straight away.
      await publishNow();
      return;
    }
    setIsLoading(false);
    setShowPublishConfirm(true);
  };

  const reportPublishError = (err: any) => {
    setIsLoading(false);
    if (err?.name === "SchedulePartialFailureError") {
      // The first schedule is already in place; the second failed. Say so, so the editor does not
      // resubmit straight into "already trigger is initiated".
      ShowToastError(`${t("schedule_partial_failure")} ${getScheduleErrorText(err.cause, t)}`);
      return;
    }
    ShowToastError(getScheduleErrorText(err, t));
  };

  //saveData
  const saveContent = async (status) => {
    setIsLoading(true);
    try {
      const detailsRes: any = await createContentType(
        contentInstance,
        subCategory,
        template?.schema_document_type,
      );
      const { message, id, name } = detailsRes?.authoring_saveSchemaContent || {};
      if (message === "Successfully created!!!") {
        //ask statuscodes instead of this
        setIsLoading(false);
        setIsDraft(false);
        savedData.current = { id, name };
        if (status === "save") {
          ShowToastSuccess(`${subCategory} ${t("created_toast")}`);
        } else {
          try {
            setIsLoading(true);
            await publishSavedContent();
          } catch (err: any) {
            reportPublishError(err);
          }
        }
      } else {
        ShowToastSuccess(detailsRes.authoring_createContent.message);
      }
    } catch (err: any) {
      setIsLoading(false);
      ShowToastError(t("api_error_toast"));
    }
  };

  //updateData
  const updateContent = async (status) => {
    setIsLoading(true);
    try {
      const detailsRes: any = await updateContentType(
        savedData,
        contentInstance,
        subCategory,
        template?.schema_document_type,
      );
      const { message } = detailsRes?.authoring_updateSchemaContent || {};
      if (message === "Successfully updated!!!") {
        //ask statuscodes instead of this
        setIsLoading(false);
        setIsDraft(false);
        if (status === "update") {
          // const displayName = getDisplayContentType(subCategory);
          ShowToastSuccess(`${template?.title} ${t("updated_toast")}`);
        } else {
          try {
            setIsLoading(true);
            await publishSavedContent();
          } catch (err: any) {
            reportPublishError(err);
          }
        }
      } else {
        ShowToastSuccess(detailsRes.authoring_createContent.message);
      }
    } catch (err: any) {
      setIsLoading(false);
      ShowToastError(t("api_error_toast"));
    }
  };

  const handlePublish = () => {
    // Validate the schedule BEFORE anything is saved, so a bad time never leaves a half-done publish.
    const verdict = isCollection
      ? { valid: true as const }
      : validateScheduleChanges(initialSchedule.current, schedule);
    if (!verdict.valid) {
      const message = capitalizeFirstLetter(t(verdict.messageKey));
      setScheduleErrors({ [verdict.field]: message });
      ShowToastError(message);
      document.getElementById("schedulePublish")?.scrollIntoView?.({
        behavior: "smooth",
        block: "center",
      });
      return;
    }
    setScheduleErrors({});
    const { id } = savedData.current;
    if (isDraft) {
      //if any changes available
      if (documentId || id) {
        //if already saved
        updateContent("publish");
      } else {
        saveContent("publish");
      }
    } else {
      // publishContent();
      updateContent("publish");
    }
  };

  //GetSchema API
  const getSchema = async () => {
    try {
      // setIsLoading(true);
      setIsLoadingData(true);
      const detailsRes: any = await fetchSchema(subCategory); //change useQuery check Charts
      const schema = detailsRes?.authoring_getSchema;
      return schema;
    } catch (err: any) {
      ShowToastError(t("api_error_toast"));
    }
  };

  //GetSchemaData API
  const getSchemaWithData = async (initialValues) => {
    try {
      setIsLoadingData(true);
      const detailsRes: any = await fetchSchemaWithData(subCategory, documentId, category);
      const schema = detailsRes?.authoring_getSchemaContent;
      // calculateInitialValuesWithData only copies keys the schema itself declares as
      // fields — when the schema has no declared slug field (the case the generic SEO
      // slug field targets), the real stored slug would otherwise be silently dropped.
      const initialData = calculateInitialValuesWithData(initialValues, schema);
      // The backend can return the brand id as a number (e.g. 1); brand fields are always strings.
      if (Object.hasOwn(initialData, "brand_name_x_str") && initialData.brand_name_x_str != null) {
        initialData.brand_name_x_str = String(initialData.brand_name_x_str);
      }
      const documentBrand =
        schema?.brand_name_x_str || schema?.responseBrandname || schema?.brand_name;
      const finalschema = applyContentFieldDefaults(
        {
          ...initialData,
          slug: schema?.slug,
          // Not schema fields (the schema never declares them), so they must be carried explicitly:
          // the primary keeps canonical/saves on the document's own brand, and the secondary list
          // survives an edit (the update replaces the whole document).
          brand_name: documentBrand == null ? documentBrand : String(documentBrand),
          // Shown as-is in the SEO slug field until the slug changes.
          ...(schema?.canonical_url ? { canonical_url: schema.canonical_url } : {}),
          ...(Array.isArray(schema?.secondary_brandname)
            ? { secondary_brandname: schema.secondary_brandname }
            : {}),
        },
        subCategory,
      );
      setContentInstance(finalschema);
      // Seed the form with the schedules still to come (shown locked); one that has already fired is
      // history.
      const { initial, pending } = scheduleFromTriggers(schema);
      setPendingSchedule(pending);
      initialSchedule.current = initial;
      setSchedule(initial);
      savedData.current = {
        id: schema?.document_id,
        name: schema?.name,
        canonical: schema?.canonical_url
          ? { path: schema.canonical_url, slug: schema?.slug || "" }
          : undefined,
      };
    } catch (err: any) {
      ShowToastError(t("api_error_toast"));
    }
  };

  const getSchemaAndData = async () => {
    try {
      setIsLoading(true);
      setIsLoadingData(true);
      const schema = await getSchema();
      setTemplate(schema);
      if ((schema?.fields?.length ?? 0) > 0) {
        const initialValues = await calculateInitialValues(schema?.fields);
        if (documentId) {
          //On Edit Flow
          await getSchemaWithData(initialValues);
        } else {
          //On Create Flow
          setContentInstance({ ...initialValues });
        }
      } else {
        ShowToastError(t("api_error_toast"));
        setIsLoading(false);
        setIsLoadingData(false);
      }
    } catch (err: any) {
      ShowToastError(t("api_error_toast"));
    } finally {
      setIsLoading(false);
      setIsLoadingData(false);
    }
  };

  //get Schema Details
  useEffect(() => {
    if (subCategory) {
      getSchemaAndData();
    }
  }, [subCategory]);
  const scheduleConfirmation = isCollection ? null : getScheduleConfirmation(schedule);
  return (
    <>
      {isLoading || isLoadingData ? <Loader /> : null}
      {!isLoadingData ? (
        <DynamicComponent
          contentType={subCategory}
          type={category}
          template={template}
          handleSave={updateContent}
          handlePublish={handlePublish}
          contentInstance={contentInstance}
          setContentInstance={setContentInstance}
          isEdit={documentId ? true : false}
          // setPageState={setPageState}
          listUrl={category}
          schedule={isCollection ? undefined : schedule}
          setSchedule={isCollection ? undefined : handleScheduleChange}
          scheduleErrors={scheduleErrors}
          schedulePending={pendingSchedule}
        />
      ) : null}
      {showPublishConfirm ? (
        <CommonPlateformXDialog
          isDialogOpen={showPublishConfirm}
          title={t("congratulations")}
          subTitle={
            showPublishConfirm
              ? `${t("your")} ${template?.title} ${
                  scheduleConfirmation
                    ? t(scheduleConfirmation.key, scheduleConfirmation.params)
                    : t("publish_popup_message")
                }`
              : t("requested_action")
          }
          closeButtonHandle={handleCloseDialog}
          confirmButtonText={t("go_to_listing")}
          confirmButtonHandle={() => navigate(`/${category}/${subCategory}`)}
          modalType='publish'
        />
      ) : null}
    </>
  );
};
